import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Camera, Car, Copy, Download, FileArchive, Paperclip, Pencil, Plus, Receipt, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import PageHeader, { card, ghostBtn } from "@/app/PageHeader";
import { useSession } from "@/app/session";

// Business expenses (Schedule C categories) with receipt photos, a mileage log at the IRS standard rate,
// and a yearly package for the accountant. Backend: backend/expenses.py at /api/expenses/*.

const field = "h-10 w-full rounded-md border border-white/10 bg-vortex-ink/60 px-3 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-vortex-cyan";
const label = "font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase";
const btnPrimary = "flex items-center justify-center gap-2 rounded-full bg-vortex-cyan px-5 py-2.5 font-mono text-xs tracking-[0.2em] uppercase text-vortex-ink transition-all duration-300 hover:shadow-[0_0_30px_rgba(92,179,226,0.4)] disabled:opacity-50";
const START_KEY = "vortex_trip_start";

const money = (n) => (n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const shortDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Phone photos are several MB; shrink to 2000px JPEG so receipts stay readable but small.
const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error("Could not read that file."));
  reader.onload = () => {
    if (!file.type.startsWith("image/") || file.type === "image/heic") { resolve(reader.result); return; }
    const img = new Image();
    img.onerror = () => resolve(reader.result);
    img.onload = () => {
      const scale = Math.min(1, 2000 / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

const Field = ({ id, title, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <label className={label} htmlFor={id}>{title}</label>
    {children}
  </div>
);

const LinkPickers = ({ meta, value, onChange }) => {
  const machines = meta.machines.filter((m) => !value.location_id || m.location_id === value.location_id);
  return (
    <>
      <Field id="link-location" title="Location (optional)">
        <select id="link-location" className={field} value={value.location_id}
          onChange={(e) => onChange({ location_id: e.target.value, machine_id: "" })}>
          <option value="">Not tied to a location</option>
          {meta.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </Field>
      <Field id="link-machine" title="Machine (optional)">
        <select id="link-machine" className={field} value={value.machine_id}
          onChange={(e) => {
            const m = meta.machines.find((x) => x.id === e.target.value);
            onChange({ machine_id: e.target.value, location_id: m?.location_id || value.location_id });
          }}>
          <option value="">Not tied to a machine</option>
          {machines.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      </Field>
    </>
  );
};

const blankExpense = () => ({ date: today(), amount: "", sales_tax: "", tax_exempt: false, category: "inventory", vendor: "", description: "",
  payment_method: "", location_id: "", machine_id: "", receipt_id: "" });

const ExpenseDialog = ({ open, initial, meta, onClose, onSaved, openReceipt }) => {
  const { api, onAuthError } = useSession();
  const [form, setForm] = useState(blankExpense);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({ ...blankExpense(), ...initial }); }, [open, initial]);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const attach = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const data = await fileToDataUrl(file);
      const res = await api.post("/expenses/receipts", { data, filename: file.name });
      set({ receipt_id: res.data.id });
      toast.success("Receipt attached.");
    } catch (err) {
      onAuthError(err, err.message || "Couldn't upload the receipt.");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...form, amount: Number(form.amount), sales_tax: form.tax_exempt ? 0 : Number(form.sales_tax) || 0 };
      delete body.id;
      const res = form.id ? await api.put(`/expenses/${form.id}`, body) : await api.post("/expenses", body);
      onSaved(res.data);
    } catch (err) {
      onAuthError(err, "Couldn't save the expense. Check the date, amount and category.");
    } finally {
      setSaving(false);
    }
  };

  const cat = meta.categories.find((c) => c.key === form.category);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto border-white/10 bg-vortex-surface text-slate-100 sm:max-w-lg" data-testid="expense-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold tracking-tight text-white">{form.id ? "Edit expense" : "Add an expense"}</DialogTitle>
          <DialogDescription className="text-sm text-slate-400">Snap the receipt, then fill in what it was for.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-4">
          <div className="col-span-2 flex flex-wrap items-center gap-2">
            <label className={`${ghostBtn} cursor-pointer`}>
              <Camera size={12} /> {uploading ? "Uploading..." : form.receipt_id ? "Replace receipt" : "Take photo"}
              <input type="file" accept="image/*" capture="environment" className="sr-only" disabled={uploading}
                onChange={(e) => { attach(e.target.files[0]); e.target.value = ""; }} data-testid="receipt-camera" />
            </label>
            <label className={`${ghostBtn} cursor-pointer`}>
              <Paperclip size={12} /> Choose file
              <input type="file" accept="image/*,application/pdf" className="sr-only" disabled={uploading}
                onChange={(e) => { attach(e.target.files[0]); e.target.value = ""; }} data-testid="receipt-file" />
            </label>
            {form.receipt_id && (
              <>
                <button type="button" className="text-xs text-vortex-cyan hover:underline" onClick={() => openReceipt(form.receipt_id)}>View receipt</button>
                <button type="button" className="text-xs text-slate-500 hover:text-red-300" onClick={() => set({ receipt_id: "" })}>Remove</button>
              </>
            )}
          </div>
          <Field id="exp-date" title="Date">
            <input id="exp-date" type="date" required className={field} value={form.date} onChange={(e) => set({ date: e.target.value })} />
          </Field>
          <Field id="exp-amount" title="Amount ($)">
            <input id="exp-amount" type="number" inputMode="decimal" step="0.01" min="0.01" required className={field}
              value={form.amount} onChange={(e) => set({ amount: e.target.value })} placeholder="0.00" />
          </Field>
          <Field id="exp-tax" title="Sales tax in that ($)">
            <input id="exp-tax" type="number" inputMode="decimal" step="0.01" min="0" className={field} disabled={form.tax_exempt}
              value={form.tax_exempt ? "" : form.sales_tax} onChange={(e) => set({ sales_tax: e.target.value })} placeholder={form.tax_exempt ? "exempt" : "0.00"} data-testid="expense-sales-tax" />
          </Field>
          <label className="flex items-end gap-2 pb-2.5 text-xs text-slate-300">
            <input type="checkbox" className="accent-[#5CB3E2]" checked={form.tax_exempt} onChange={(e) => set({ tax_exempt: e.target.checked })} />
            Bought tax exempt
          </label>
          <Field id="exp-category" title="Category" className="col-span-2">
            <select id="exp-category" className={field} value={form.category} onChange={(e) => set({ category: e.target.value })}>
              {meta.categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            {cat && <p className="text-xs text-slate-500">Schedule C {cat.line.startsWith("Part") ? cat.line : `line ${cat.line}`}</p>}
          </Field>
          <Field id="exp-vendor" title="Vendor">
            <input id="exp-vendor" className={field} value={form.vendor} onChange={(e) => set({ vendor: e.target.value })} placeholder="Costco" maxLength={120} />
          </Field>
          <Field id="exp-paid" title="Paid with">
            <input id="exp-paid" className={field} value={form.payment_method} onChange={(e) => set({ payment_method: e.target.value })} placeholder="Business card" maxLength={60} list="paid-with" />
            <datalist id="paid-with"><option value="Business card" /><option value="Business checking" /><option value="Personal card" /><option value="Cash" /></datalist>
          </Field>
          <Field id="exp-desc" title="What it was for" className="col-span-2">
            <input id="exp-desc" className={field} value={form.description} onChange={(e) => set({ description: e.target.value })} placeholder="Drinks and snacks for the Acme cooler" maxLength={500} />
          </Field>
          <LinkPickers meta={meta} value={form} onChange={set} />
          <div className="col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" className={ghostBtn} onClick={onClose}>Cancel</button>
            <button type="submit" className={btnPrimary} disabled={saving || uploading} data-testid="expense-save">{saving ? "Saving..." : "Save"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const readStart = () => { try { return localStorage.getItem(START_KEY) || ""; } catch { return ""; } };
const blankTrip = () => ({ date: today(), purpose: "", start: readStart(), destination: "", miles: "", round_trip: true,
  odometer_start: "", odometer_end: "", location_id: "", machine_id: "" });

const TripDialog = ({ open, initial, meta, onClose, onSaved }) => {
  const { api, onAuthError } = useSession();
  const [form, setForm] = useState(blankTrip);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({ ...blankTrip(), ...initial }); }, [open, initial]);
  const set = (patch) => setForm((f) => {
    const next = { ...f, ...patch };
    // Odometer readings fill in the miles (the whole drive, so not doubled).
    if (("odometer_start" in patch || "odometer_end" in patch) && next.odometer_start !== "" && next.odometer_end !== "") {
      const d = Number(next.odometer_end) - Number(next.odometer_start);
      if (d > 0) Object.assign(next, { miles: String(Math.round(d * 10) / 10), round_trip: false });
    }
    return next;
  });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...form, miles: Number(form.miles),
        odometer_start: form.odometer_start === "" || form.odometer_start == null ? null : Number(form.odometer_start),
        odometer_end: form.odometer_end === "" || form.odometer_end == null ? null : Number(form.odometer_end) };
      ["id", "total_miles", "rate", "deduction", "created_at", "updated_at"].forEach((k) => delete body[k]);
      try { if (form.start) localStorage.setItem(START_KEY, form.start); } catch { /* storage blocked */ }
      const res = form.id ? await api.put(`/expenses/trips/${form.id}`, body) : await api.post("/expenses/trips", body);
      onSaved(res.data);
    } catch (err) {
      onAuthError(err, "Couldn't save the trip. A date, miles and a business purpose are required.");
    } finally {
      setSaving(false);
    }
  };

  const rate = meta.rates[form.date?.slice(0, 4)] ?? meta.rates[Object.keys(meta.rates).sort().pop()];
  const miles = (Number(form.miles) || 0) * (form.round_trip ? 2 : 1);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto border-white/10 bg-vortex-surface text-slate-100 sm:max-w-lg" data-testid="trip-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold tracking-tight text-white">{form.id ? "Edit trip" : "Log a trip"}</DialogTitle>
          <DialogDescription className="text-sm text-slate-400">The IRS asks for the date, miles, where you went and why.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-4">
          <Field id="trip-date" title="Date">
            <input id="trip-date" type="date" required className={field} value={form.date} onChange={(e) => set({ date: e.target.value })} />
          </Field>
          <Field id="trip-miles" title={form.round_trip ? "Miles one way" : "Miles"}>
            <input id="trip-miles" type="number" inputMode="decimal" step="0.1" min="0.1" required className={field}
              value={form.miles} onChange={(e) => set({ miles: e.target.value })} placeholder="0.0" />
          </Field>
          <label className="col-span-2 -mt-2 flex items-center gap-2 text-xs text-slate-300">
            <input type="checkbox" className="accent-[#5CB3E2]" checked={form.round_trip} onChange={(e) => set({ round_trip: e.target.checked })} />
            Round trip (counts the miles twice)
          </label>
          <Field id="trip-purpose" title="Business purpose" className="col-span-2">
            <input id="trip-purpose" required className={field} value={form.purpose} onChange={(e) => set({ purpose: e.target.value })} placeholder="Restock Acme cooler" maxLength={300} />
          </Field>
          <Field id="trip-from" title="From">
            <input id="trip-from" className={field} value={form.start} onChange={(e) => set({ start: e.target.value })} placeholder="Home" maxLength={200} />
          </Field>
          <Field id="trip-to" title="To">
            <input id="trip-to" className={field} value={form.destination} onChange={(e) => set({ destination: e.target.value })} placeholder="Acme HQ, Exton" maxLength={200} />
          </Field>
          <Field id="trip-odo-start" title="Odometer start (optional)">
            <input id="trip-odo-start" type="number" inputMode="decimal" min="0" className={field} value={form.odometer_start ?? ""} onChange={(e) => set({ odometer_start: e.target.value })} />
          </Field>
          <Field id="trip-odo-end" title="Odometer end (optional)">
            <input id="trip-odo-end" type="number" inputMode="decimal" min="0" className={field} value={form.odometer_end ?? ""} onChange={(e) => set({ odometer_end: e.target.value })} />
          </Field>
          <LinkPickers meta={meta} value={form} onChange={(patch) => {
            const loc = meta.locations.find((l) => l.id === (patch.location_id ?? form.location_id));
            set({ ...patch, ...(loc && !form.destination ? { destination: loc.address || loc.name } : {}) });
          }} />
          <p className="col-span-2 text-sm text-slate-300">
            {miles.toFixed(1)} mi × ${rate?.toFixed(3)} = <span className="font-semibold text-white">{money(miles * (rate || 0))}</span>
          </p>
          <div className="col-span-2 flex justify-end gap-2">
            <button type="button" className={ghostBtn} onClick={onClose}>Cancel</button>
            <button type="submit" className={btnPrimary} disabled={saving} data-testid="trip-save">{saving ? "Saving..." : "Save"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Stat = ({ title, value, sub, tone = "text-white" }) => (
  <div className={`${card} p-5`}>
    <p className={label}>{title}</p>
    <p className={`mt-2 font-heading text-2xl font-bold ${tone}`}>{value}</p>
    {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
  </div>
);

const TABS = [["overview", "Overview"], ["expenses", "Expenses"], ["mileage", "Mileage"]];

const Expenses = () => {
  const { api, onAuthError } = useSession();
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [tab, setTab] = useState("overview");
  const [meta, setMeta] = useState(null);
  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [trips, setTrips] = useState([]);
  const [catFilter, setCatFilter] = useState("");
  const [expDialog, setExpDialog] = useState(null);   // null = closed, {} = new, expense = edit
  const [tripDialog, setTripDialog] = useState(null);
  const [rateDraft, setRateDraft] = useState("");

  const load = useCallback(async () => {
    try {
      const [m, s, e, t] = await Promise.all([
        api.get("/expenses/meta"), api.get("/expenses/summary", { params: { year } }),
        api.get("/expenses", { params: { year } }), api.get("/expenses/trips", { params: { year } })]);
      setMeta(m.data); setSummary(s.data); setExpenses(e.data.expenses); setTrips(t.data.trips);
    } catch (err) {
      onAuthError(err, "Couldn't load expenses.");
    }
  }, [api, onAuthError, year]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (meta) setRateDraft(String(meta.rates[String(year)] ?? "")); }, [meta, year]);

  const cats = useMemo(() => Object.fromEntries((meta?.categories || []).map((c) => [c.key, c])), [meta]);
  const names = useMemo(() => Object.fromEntries([...(meta?.locations || []), ...(meta?.machines || [])].map((x) => [x.id, x.name])), [meta]);
  const shown = catFilter ? expenses.filter((e) => e.category === catFilter) : expenses;

  const download = async (path, filename) => {
    try {
      const res = await api.get(path, { params: { year }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: filename });
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      onAuthError(err, "Couldn't build the download.");
    }
  };

  const openReceipt = async (id) => {
    const win = window.open("", "_blank");
    try {
      const res = await api.get(`/expenses/receipts/${id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (win) win.location.href = url; else window.location.href = url;
    } catch (err) {
      win?.close();
      onAuthError(err, "Couldn't open the receipt.");
    }
  };

  const remove = async (kind, item) => {
    const what = kind === "trip" ? `the ${item.total_miles} mi trip on ${shortDate(item.date)}` : `the ${money(item.amount)} expense on ${shortDate(item.date)}`;
    if (!window.confirm(`Delete ${what}?`)) return;
    try {
      await api.delete(kind === "trip" ? `/expenses/trips/${item.id}` : `/expenses/${item.id}`);
      toast.success("Deleted.");
      load();
    } catch (err) {
      onAuthError(err, "Couldn't delete it.");
    }
  };

  const saveRate = async () => {
    const r = Number(rateDraft);
    if (!(r > 0 && r < 5)) { toast.error("Enter the rate in dollars per mile, like 0.725."); return; }
    try {
      await api.put("/expenses/rates", { rates: { ...Object.fromEntries(Object.entries(meta.rates)), [String(year)]: r } });
      toast.success(`Mileage rate for ${year} saved.`);
      load();
    } catch (err) {
      onAuthError(err, "Couldn't save the rate.");
    }
  };

  const saved = (kind) => () => {
    toast.success(kind === "trip" ? "Trip saved." : "Expense saved.");
    kind === "trip" ? setTripDialog(null) : setExpDialog(null);
    load();
  };

  const years = [];
  for (let y = thisYear + 1; y >= thisYear - 4; y -= 1) years.push(y);

  if (!meta || !summary) return <p className="text-sm text-slate-500">Loading...</p>;

  const maxMonth = Math.max(1, ...summary.months.map((m) => m.expenses + m.mileage));
  return (
    <div data-testid="expenses-page">
      <PageHeader title="Expenses & Mileage"
        intro="Log what the business spends and the miles you drive, with the receipt attached. At tax time, download one file for your accountant."
        actions={(
          <>
            <select aria-label="Tax year" className={field.replace("w-full", "w-28")} value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="expense-year">
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <button className={ghostBtn} onClick={() => setTripDialog({})} data-testid="add-trip"><Car size={12} /> Log trip</button>
            <button className={btnPrimary} onClick={() => setExpDialog({})} data-testid="add-expense"><Plus size={14} /> Expense</button>
          </>
        )} />

      <div className="mb-6 flex gap-1 border-b border-white/10" role="tablist">
        {TABS.map(([key, title]) => (
          <button key={key} role="tab" aria-selected={tab === key} onClick={() => setTab(key)} data-testid={`expense-tab-${key}`}
            className={`-mb-px border-b-2 px-4 py-2 text-sm transition-colors ${tab === key ? "border-vortex-cyan text-white" : "border-transparent text-slate-400 hover:text-slate-200"}`}>
            {title}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-6" data-testid="expense-overview">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Stat title="Est. deductions" value={money(summary.estimated_deductions)} tone="text-vortex-cyan"
              sub={summary.meals_nondeductible ? `After the 50% meals limit (${money(summary.meals_nondeductible)})` : "Expenses plus mileage"} />
            <Stat title="Expenses" value={money(summary.expense_total)}
              sub={`${summary.expense_count} logged${summary.sales_tax_paid ? ` · ${money(summary.sales_tax_paid)} was sales tax` : ""}`} />
            <Stat title="Mileage" value={money(summary.mileage_deduction)} sub={`${summary.miles.toLocaleString()} mi on ${summary.trip_count} trips`} />
            <Stat title="Missing receipts" value={summary.missing_receipts} tone={summary.missing_receipts ? "text-amber-300" : "text-emerald-300"}
              sub={summary.missing_receipts ? "Attach them while you still have them" : "Every expense has one"} />
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className={`${card} p-5`}>
              <p className={label}>By Schedule C line</p>
              {summary.categories.length === 0 && summary.trip_count === 0 ? (
                <p className="mt-4 text-sm text-slate-500">Nothing logged for {year} yet.</p>
              ) : (
                <table className="mt-3 w-full text-sm">
                  <tbody>
                    {summary.categories.map((c) => (
                      <tr key={c.key} className="border-b border-white/5">
                        <td className="py-2 pr-2 text-slate-300">{c.label}</td>
                        <td className="py-2 pr-2 font-mono text-xs text-slate-500">{c.line.startsWith("Part") ? "COGS" : c.line}</td>
                        <td className="py-2 text-right text-slate-100">{money(c.total)}</td>
                      </tr>
                    ))}
                    {summary.trip_count > 0 && (
                      <tr className="border-b border-white/5">
                        <td className="py-2 pr-2 text-slate-300">Mileage ({summary.miles.toLocaleString()} mi)</td>
                        <td className="py-2 pr-2 font-mono text-xs text-slate-500">9</td>
                        <td className="py-2 text-right text-slate-100">{money(summary.mileage_deduction)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>

            <div className={`${card} p-5`}>
              <p className={label}>By month</p>
              <div className="mt-4 flex items-end gap-1.5" aria-label="Spending by month">
                {summary.months.map((m) => (
                  <div key={m.month} className="flex flex-1 flex-col items-center gap-1" title={`${MONTHS[m.month - 1]}: ${money(m.expenses)} expenses, ${money(m.mileage)} mileage`}>
                    <div className="flex h-32 w-full flex-col justify-end">
                      <div className="w-full rounded-t-sm bg-vortex-cyan/40" style={{ height: `${(m.mileage / maxMonth) * 100}%` }} />
                      <div className="w-full bg-vortex-cyan" style={{ height: `${(m.expenses / maxMonth) * 100}%` }} />
                    </div>
                    <span className="text-[10px] text-slate-500">{MONTHS[m.month - 1][0]}</span>
                  </div>
                ))}
              </div>
              <p className="mt-3 flex gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 bg-vortex-cyan" /> Expenses</span>
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 bg-vortex-cyan/40" /> Mileage</span>
              </p>
            </div>
          </div>

          <div className={`${card} p-5`}>
            <p className={label}>For your accountant</p>
            {summary.sales_tax_paid > 0 && (
              <p className="mt-2 text-sm text-slate-300" data-testid="sales-tax-paid">
                Sales tax paid in {year}: <b className="text-white">{money(summary.sales_tax_paid)}</b>
                {summary.sales_tax_on_inventory > 0 && <>, {money(summary.sales_tax_on_inventory)} of it on product you resell</>}
                {summary.tax_exempt_count > 0 && <> · {summary.tax_exempt_count} tax-exempt purchase{summary.tax_exempt_count === 1 ? "" : "s"}</>}.
              </p>
            )}
            {summary.waste_units > 0 && (
              <p className="mt-2 text-sm text-slate-300" data-testid="waste-total">
                Waste in {year}: <b className="text-white">{summary.waste_units} unit{summary.waste_units === 1 ? "" : "s"}</b>
                {summary.waste_cost > 0 && <>, {money(summary.waste_cost)} at cost</>}. It's already counted in your inventory purchases, so it isn't added again.
              </p>
            )}
            <p className="mt-2 text-sm text-slate-400">The package has a summary by Schedule C line, the expense log, the mileage log, and every receipt named to match its row.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button className={btnPrimary} onClick={() => download("/expenses/export/package.zip", `vortex-taxes-${year}.zip`)} data-testid="export-package">
                <FileArchive size={14} /> {year} tax package (.zip)
              </button>
              <button className={ghostBtn} onClick={() => download("/expenses/export/expenses.csv", `vortex-expenses-${year}.csv`)}><Download size={12} /> Expenses CSV</button>
              <button className={ghostBtn} onClick={() => download("/expenses/export/mileage.csv", `vortex-mileage-${year}.csv`)}><Download size={12} /> Mileage CSV</button>
            </div>
            <p className="mt-4 text-xs text-slate-500">
              If you take the standard mileage rate, don't also log gas, insurance or repairs for the car; parking and tolls still count.
              Estimates only, so check with your accountant.
            </p>
          </div>
        </div>
      )}

      {tab === "expenses" && (
        <div data-testid="expense-list">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <select aria-label="Filter by category" className={field.replace("w-full", "w-full sm:w-72")} value={catFilter} onChange={(e) => setCatFilter(e.target.value)}>
              <option value="">All categories</option>
              {meta.categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            <p className="text-sm text-slate-400">{shown.length} · {money(shown.reduce((s, e) => s + e.amount, 0))}</p>
          </div>
          {shown.length === 0 ? (
            <div className={`${card} p-10 text-center text-sm text-slate-500`}>
              No expenses for {year}{catFilter ? " in this category" : ""}. Tap <span className="text-slate-300">Expense</span> and snap the receipt.
            </div>
          ) : (
            <ul className={card}>
              {shown.map((e) => (
                <li key={e.id} className="flex items-start gap-4 border-b border-white/5 px-4 py-3 last:border-0">
                  <span className="w-14 shrink-0 pt-0.5 text-xs text-slate-500">{shortDate(e.date)}</span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-slate-100">{e.vendor || cats[e.category]?.label}{e.description && <span className="text-slate-400"> · {e.description}</span>}</p>
                    <p className="truncate text-xs text-slate-500">
                      {[cats[e.category]?.label, names[e.machine_id] || names[e.location_id], e.payment_method,
                        e.tax_exempt ? "tax exempt" : e.sales_tax ? `${money(e.sales_tax)} tax` : ""].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  <span className="shrink-0 text-sm font-medium text-white">{money(e.amount)}</span>
                  <div className="flex shrink-0 items-center gap-1">
                    {e.receipt_id ? (
                      <button className="rounded p-1.5 text-vortex-cyan hover:bg-white/5" onClick={() => openReceipt(e.receipt_id)} aria-label="View receipt" title="View receipt"><Receipt size={15} /></button>
                    ) : (
                      <button className="rounded p-1.5 text-amber-300/80 hover:bg-white/5" onClick={() => setExpDialog(e)} aria-label="Attach receipt" title="No receipt yet"><Receipt size={15} /></button>
                    )}
                    <button className="rounded p-1.5 text-slate-400 hover:bg-white/5 hover:text-white" onClick={() => setExpDialog(e)} aria-label="Edit"><Pencil size={14} /></button>
                    <button className="rounded p-1.5 text-slate-500 hover:bg-white/5 hover:text-red-300" onClick={() => remove("expense", e)} aria-label="Delete"><Trash2 size={14} /></button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {tab === "mileage" && (
        <div className="space-y-4" data-testid="trip-list">
          <div className={`${card} flex flex-wrap items-end gap-3 p-4`}>
            <Field id="rate" title={`IRS rate for ${year} ($/mile)`}>
              <input id="rate" type="number" step="0.001" min="0" className={field.replace("w-full", "w-36")} value={rateDraft} onChange={(e) => setRateDraft(e.target.value)} placeholder="0.725" />
            </Field>
            <button className={ghostBtn} onClick={saveRate} disabled={Number(rateDraft) === meta.rates[String(year)]}>Save rate</button>
            <p className="pb-2 text-xs text-slate-500">
              {summary.miles.toLocaleString()} mi · {money(summary.mileage_deduction)}. The IRS sets a new rate each January; update it here when it does.
            </p>
          </div>
          {trips.length === 0 ? (
            <div className={`${card} p-10 text-center text-sm text-slate-500`}>No trips for {year}. Log each drive to a location, the store, or a sales visit.</div>
          ) : (
            <ul className={card}>
              {trips.map((t) => (
                <li key={t.id} className="flex items-start gap-4 border-b border-white/5 px-4 py-3 last:border-0">
                  <span className="w-14 shrink-0 pt-0.5 text-xs text-slate-500">{shortDate(t.date)}</span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-slate-100">{t.purpose}</p>
                    <p className="truncate text-xs text-slate-500">
                      {[[t.start, t.destination].filter(Boolean).join(" to "), t.round_trip && "round trip", names[t.machine_id] || names[t.location_id]].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  <span className="shrink-0 text-right text-sm text-white">{t.total_miles} mi<span className="block text-xs text-slate-500">{money(t.deduction)}</span></span>
                  <div className="flex shrink-0 items-center gap-1">
                    <button className="rounded p-1.5 text-slate-400 hover:bg-white/5 hover:text-white" title="Log this trip again today" aria-label="Log again today"
                      onClick={() => setTripDialog({ ...t, id: undefined, date: today(), odometer_start: "", odometer_end: "" })}><Copy size={14} /></button>
                    <button className="rounded p-1.5 text-slate-400 hover:bg-white/5 hover:text-white" onClick={() => setTripDialog(t)} aria-label="Edit"><Pencil size={14} /></button>
                    <button className="rounded p-1.5 text-slate-500 hover:bg-white/5 hover:text-red-300" onClick={() => remove("trip", t)} aria-label="Delete"><Trash2 size={14} /></button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <ExpenseDialog open={!!expDialog} initial={expDialog} meta={meta} onClose={() => setExpDialog(null)} onSaved={saved("expense")} openReceipt={openReceipt} />
      <TripDialog open={!!tripDialog} initial={tripDialog} meta={meta} onClose={() => setTripDialog(null)} onSaved={saved("trip")} />
    </div>
  );
};

export default Expenses;
